import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import NotificationsNoneIcon from '@mui/icons-material/Notifications'
import { Button, Checkbox, CircularProgress, Stack, styled, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { CoreMyntModal } from 'components/modals/CoreMyntModal'
import { Access } from 'contexts/permissions'
import Colors from 'figma/panda/Colors'
import Shadows from 'figma/tokens/Shadows'
import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { transactionTriggerBusinessEventUsingPOST } from '../../api'
import { useTransactionsV2 } from '../../api/react-query'
import { BackofficeCardTxType2 } from '../../api/swagger/definitions/backoffice'
import BorderRadius from '../../figma/tokens/BorderRadius'
import ColorsDeprecated from '../../figma/tokens/ColorsDeprecated'
import Spacings from '../../figma/tokens/Spacings'
import type { BackOfficeAllTransactionDtos } from '../../helpers/ProcessHandler'
import useMaterialNotification from '../../hooks/useMaterialNotification'
import FigmaBox from '../../mynt-components/components/FigmaBox'
import { When } from '../../mynt-components/components/When'
import { CardCreditRepaymentTransactionDetails, CardCreditRepaymentTransactionRow } from './card-credit-repayment-row'
import { StandardTransactionDetails, StandardTransactionRow } from './standard-transaction-row'
import { StyledActionWrapper, TableFilters } from './support-components'
import { TopupTransactionDetails, TopupTransactionRow } from './topup-transaction-row'
import { UnknownTransactionDetails, UnknownTransactionRow } from './unknown-transaction-row'

const getTransactionRowComponents = (transaction: BackOfficeAllTransactionDtos) => {
  switch (transaction.txType) {
    case BackofficeCardTxType2.STANDARD: {
      return {
        Row: () => <StandardTransactionRow transaction={transaction} />,
        Details: () => <StandardTransactionDetails transaction={transaction} />,
      }
    }

    case BackofficeCardTxType2.CARD_CREDIT_REPAYMENT: {
      return {
        Row: () => <CardCreditRepaymentTransactionRow transaction={transaction} />,
        Details: () => <CardCreditRepaymentTransactionDetails transaction={transaction} />,
      }
    }

    case BackofficeCardTxType2.TOP_UP: {
      return {
        Row: () => <TopupTransactionRow transaction={transaction} />,
        Details: () => <TopupTransactionDetails transaction={transaction} />,
      }
    }

    default: {
      return {
        Row: () => <UnknownTransactionRow transaction={transaction} />,
        Details: () => <UnknownTransactionDetails transaction={transaction} />,
      }
    }
  }
}

interface RowProps {
  transaction: BackOfficeAllTransactionDtos
  onSelect: (id: string) => void
  selected: boolean
}

function Row({ transaction, onSelect, selected }: RowProps) {
  const { Details, Row } = useMemo(() => getTransactionRowComponents(transaction), [transaction])
  const [open, setOpen] = React.useState(false)

  return (
    <React.Fragment>
      <TableRow onClick={() => setOpen(!open)} sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} hover>
        <TableCell>
          <Access permissions="customers.cards.transactions.notify">
            <Checkbox
              checked={selected}
              onClick={(event) => event.stopPropagation()}
              onInput={() => onSelect(transaction.id)}
              size="small"
            />
          </Access>
        </TableCell>
        <Row />
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, backgroundColor: ColorsDeprecated.baseCloud, borderRadius: BorderRadius.soft }} width="100%">
              <Details />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  )
}

export const CustomerTransactionsTable = ({ customerId }) => {
  const limit = 10
  const [filters, setFilters] = useState<TableFilters>({} as TableFilters)
  const [page, setPage] = useState(0)
  const [data, setData] = useState<BackOfficeAllTransactionDtos[]>([])
  const [isNotifyTransactionOpen, setIsNotifyTransactionOpen] = useState(false)
  const [selectedTx, setSelectedTx] = useState<string[]>([])
  const [notifiedTx, setNotifiedTx] = useState<string[]>([])
  const [isNotifying, setIsNotifying] = useState(false)
  const notify = useMaterialNotification()

  const { data: transactionsData = [], isLoading } = useTransactionsV2(customerId, {
    limit,
    offset: page * limit,
    query: filters.vendor,
    cardIds: filters.cards,
    personIds: filters.persons,
    from: filters.from,
    to: filters.to,
  })

  useEffect(() => {
    // Only update the data once it has loaded to prevent the table from jumping around
    if (!isLoading) setData(transactionsData)
  }, [transactionsData, isLoading])

  const bulkNotify = async () => {
    setIsNotifying(true)

    try {
      await selectedTx.reduce((promise, txId) => {
        return promise.then(() => {
          return transactionTriggerBusinessEventUsingPOST(customerId, txId).then(() => {
            setNotifiedTx((prev) => [...prev, txId])
          })
        })
      }, Promise.resolve())

      setSelectedTx([])
      notify(`Transaction notifications sent successfully`)
      setIsNotifyTransactionOpen(false)
    } catch {
      setIsNotifyTransactionOpen(false)
    } finally {
      setIsNotifying(false)
      setNotifiedTx([])
    }
  }

  const handleCloseNotify = () => {
    if (isNotifying) return

    setIsNotifyTransactionOpen(false)
  }

  const handleSelect = (id: string) => {
    setSelectedTx((prev) => (prev.includes(id) ? prev.filter((txId) => txId !== id) : [...prev, id]))
  }

  const isAllChecked = data.map((tx) => tx.id).every((id) => selectedTx.includes(id))

  const handleSelectAll = () => {
    if (isAllChecked) {
      setSelectedTx([])
    } else {
      setSelectedTx(data.map((tx) => tx.id))
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    // Reset page if any of the filters change.
    // If there is only a few results and the user is on page >2 then the table will be empty
    setPage(0)
  }, [filters])

  return (
    <>
      <CoreMyntModal
        onClose={handleCloseNotify}
        title={isNotifying ? 'Sending notifications...' : 'Are you sure?'}
        open={isNotifyTransactionOpen}
        actions={(Wrapper) => (
          <Wrapper>
            <Button disabled={isNotifying} onClick={handleCloseNotify}>
              Cancel
            </Button>
            <Button disabled={isNotifying} variant="contained" color="primary" onClick={bulkNotify}>
              <Box display="flex" gap={1}>
                {isNotifying && <CircularProgress />}
                Notify
              </Box>
            </Button>
          </Wrapper>
        )}
      >
        <>
          {!isNotifying && (
            <Typography variant="body2" color={Colors.base500}>
              This will send {selectedTx.length} transaction{selectedTx.length !== 1 ? 's' : ''} notification to the customer.
            </Typography>
          )}
          {isNotifying && (
            <Typography variant="body2" color={Colors.base500}>
              {notifiedTx.length}/{selectedTx.length} transaction{notifiedTx.length !== 1 ? 's' : ''} successfully notified.
            </Typography>
          )}
        </>
      </CoreMyntModal>
      <FigmaBox bottom={Spacings.tiny} direction="row" fullWidth justify="flex-end">
        <TableFilters onFilterChange={(filters) => setFilters(filters)} customerId={customerId} />
      </FigmaBox>
      <Paper>
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox checked={isAllChecked} onInput={handleSelectAll} size="small" />
                </TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>Card holder</TableCell>
                <TableCell>Card</TableCell>
                <TableCell>Amount</TableCell>
              </TableRow>
              <TableCell style={{ opacity: isLoading ? 1 : 0, border: 'none' }} padding="none" colSpan={100}>
                <LinearProgress />
              </TableCell>
            </TableHead>
            <TableBody>
              <When is={data.length === 0 && !isLoading}>
                <TableCell colSpan={100}>
                  <FigmaBox alignText="center" fullWidth justify="center" spacing={Spacings.medium} fullPadding>
                    <h2>Customer has no transactions</h2>
                  </FigmaBox>
                </TableCell>
              </When>
              {data.map((transaction) => (
                <Row
                  key={transaction.id}
                  onSelect={handleSelect}
                  transaction={transaction}
                  selected={selectedTx.includes(transaction.id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={-1}
          rowsPerPage={limit}
          page={page}
          onPageChange={async (event, newPage) => {
            setPage(newPage)
          }}
        />
      </Paper>
      {selectedTx.length > 0 && (
        <ActionMenu total={data.length} selected={selectedTx.length} onNotify={() => setIsNotifyTransactionOpen(true)} />
      )}
    </>
  )
}

interface ActionMenuProps {
  total: number
  selected: number
  onNotify: () => void
}

const padding = {
  padding: 1.5,
  paddingLeft: 2.5,
  paddingRight: 2.5,
}

const ActionMenu = ({ total, selected, onNotify }: ActionMenuProps) => {
  return (
    <ActionMenuContainer>
      <Stack direction="row" alignItems="center">
        <Typography sx={{ ...padding }} color={Colors.baseWhite} variant="body2">
          <b>{selected}</b> of <b>{total}</b> selected
        </Typography>
        <ActionStack
          onClick={onNotify}
          sx={{ borderTopRightRadius: 16, borderBottomRightRadius: 16, ...padding }}
          direction="row"
          alignItems="center"
          gap={0.5}
        >
          <Box fontSize={20} lineHeight={0}>
            <NotificationsNoneIcon fontSize="inherit" />
          </Box>
          <Typography color={Colors.baseWhite} variant="body2">
            Notify transactions
          </Typography>
        </ActionStack>
      </Stack>
    </ActionMenuContainer>
  )
}

const ActionStack = styled(Stack)`
  @keyframes fadein {
    from { opacity: 0; transform: translate(-50%, 20px); }
    to { opacity: 1; transform: translate(-50%, 0);}
  }

  cursor: pointer;
  transition: all 600ms cubic-bezier(0.16, 1, 0.3, 1);
  background-color: ${Colors.bluePrimary600Cta};
  
  :hover {
    background-color: ${Colors.bluePrimary500};
  } 
`

const ActionMenuContainer = styled(Box)(({ theme }) => ({
  zIndex: 1000,
  position: 'fixed',
  animation: 'fadein 600ms forwards cubic-bezier(0.16, 1, 0.3, 1);',
  bottom: 32,
  backgroundColor: Colors.bluePrimary600Cta,
  color: Colors.baseWhite,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 16,
  borderColor: Colors.base500,
  boxShadow: Shadows.baseSoft,
  left: '50%',
  transform: 'translateX(50%)',
}))
